import React, { useState } from "react";
import { Typography, Box, Grid2, ListItemText, useTheme, Skeleton, ListItem, ListItemIcon, List } from "@mui/material";
import styles from "../../../styles/ProjectList.module.css";
import Highcharts from "highcharts/highstock";
import PieChart from "highcharts-react-official";
import { RxSize } from "react-icons/rx";
import { useGetOpenIssuesDistributionSeverityQuery } from '../../../store/slices/apis/issuesDashboardSlice';
import IssuesPopup from "./IssuesPopup";
import { useParams } from "react-router-dom";
import { FaSquareFull } from "react-icons/fa6";


const OpenIssueDistributionGraph = () => {
  const { projectId } = useParams();
  const [popupData, setPopupData] = useState(null);
  const [open, setOpen] = useState(false);
  const theme = useTheme();
  const { data: OpenIssuesDistributionSeverity = [], isLoading } = useGetOpenIssuesDistributionSeverityQuery({
    projectId: projectId
  });

  const capitalLetter = (str) => {
    return str?.charAt(0).toUpperCase() + str?.slice(1).toLowerCase();
  };

  const totalCount = OpenIssuesDistributionSeverity?.data?.reduce((sum, item) => sum + item.count, 0) || 0;

  const moduleColorMap = {
    'low': '#3E756D',
    'medium': '#FFBF00',
    'high': '#FF7E5C',
    'critical': '#E72B33',
  };
  const openIssuesData = OpenIssuesDistributionSeverity?.data?.map((item, index) => ({
    y: item.count,
    value: item.count,
    color: moduleColorMap[item.severity],
    name: capitalLetter(item.severity),
    severityname: item.severity,
    percentage: (item.count / totalCount) * 100,
  })) || [];

  const options = {
    chart: {
      type: "pie",
      width: 200,
      height: 200,
      marginBottom: 30,
      marginTop: 0
    },
    title: {
      text: ""
    },
    credits: {
      enabled: false
    },
    tooltip: {
      formatter: function () {
        // Customize the hover text here
        return `<b style='color: ${this.color}'>${this.name}</b>: ${this.y}`;
      }
    },
    plotOptions: {
      pie: {
        allowPointSelect: false,
        borderWidth: 0,
        borderRadius: 0,
        cursor: "pointer",
        innerSize: 80,
        dataLabels: {
          enabled: false,
        },
        showInLegend: false,

        //startAngle: -20,
        point: {
          events: {
            click: function () {
              setPopupData({
                name: this.severityname,
                value: this.y,
              });
              setOpen(true);
            }
          }
        },
      }
    },
    series: [
      {
        name: "",
        dataLabels: {
          verticalAlign: 'top',
          enabled: true,
          style: {
            fontWeight: 600,
            fontSize: '11px',
            color: '#FFFFFF',
            lineHeight: '40px',
            textOutline: 'none',
          },
          //style: { "fontSize": "11px", "fontWeight": "600"},
          //connectorWidth: 1,
          distance: -16,
          //connectorColor: '#000000',
          formatter: function () {
            return Math.round(this.percentage) + '%';
          }
        },
        color: "#006600",
        lineWidth: 1,
        marker: {
          enabled: false,
          symbol: "circle",
          radius: 3,
          states: {
            hover: {
              enabled: true,
              lineWidth: 1
            }
          }
        },
        data: openIssuesData,
      }
    ]
  };

  if (isLoading) {
    return (
      <Box sx={{ p: 2, width: "100%", height: "251px", maxWidth: "100%", border: "1px solid #D8D8D8", mt: 0, borderRadius: "12px", overflow: "hidden", backgroundColor: "white" }}>
        <Box sx={{}}>
          <Typography variant="body" sx={{ fontWeight: 500, fontSize: "18px", lineHeight: "100%", letterSpacing: '-0.4px', color: `${theme?.customSettings?.themeColor}`, }}>
            Open Issues Distribution by Severity
          </Typography>
          <Typography sx={{ mt: "5px", color: "#5052588A", fontWeight: 500, fontSize: "14px", lineHeight: "100%", letterSpacing: '-0.4px' }}>
            Issue resolution based on severity to mitigate project risks effectively
          </Typography>
        </Box>
        <Grid2 container>
          <Grid2 size={{ xs: 12, md: 7 }}>
            {[...Array(3)].map((_, index) => (
              <Grid2 container direction="row" spacing={1} key={index} sx={{ marginLeft: "10px", paddingTop: index === 0 ? '40px' : '10px' }}>
                <Box sx={{ width: "25px", padding: "8px" }}>
                  <Skeleton variant="circular" width={18} height={18} />
                </Box>
                <Box sx={{ width: "130px", padding: "8px" }}>
                  <Skeleton variant="text" width={100} height={20} />
                </Box>
                <Box sx={{ width: "30px", padding: "4px" }}>
                  <Skeleton variant="text" width={20} height={28} />
                </Box>
              </Grid2>
            ))}
          </Grid2>

          <Grid2 size={{ xs: 12, md: 5 }} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <Skeleton variant="circular" width={120} height={120} />
          </Grid2>
        </Grid2>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2, width: "100%", height: "251px", maxWidth: "100%", border: "1px solid #D8D8D8", mt: 0, borderRadius: "12px", overflow: "hidden", backgroundColor: "white" }}>
      <Box sx={{}}>
        <Typography variant="body" sx={{ fontWeight: 500, fontSize: "18px", lineHeight: "100%", letterSpacing: '-0.4px', color: `${theme?.customSettings?.themeColor}`, }}>
          Open Issues Distribution by Severity
        </Typography>
        <Typography sx={{ mt: "5px", color: "#5052588A", fontWeight: 500, fontSize: "14px", lineHeight: "100%", letterSpacing: '-0.4px' }}>
          Issue resolution based on severity to mitigate project risks effectively
        </Typography>
      </Box>

      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", }}>
        {openIssuesData?.length === 0 ? (
          <Box sx={{ width: '100%', textAlign: 'center', py: 4 }}>
            <Typography variant="body1" color="textSecondary">
              No data available to display the chart.
            </Typography>
          </Box>
        ) : (
          <>
            <List sx={{ padding: 0, mb: 4 }}>
              {openIssuesData?.map((val, index) => (
                <ListItem key={index} sx={{ paddingLeft: 0 }}>
                  <ListItemIcon sx={{ minWidth: '20px' }}>
                    <FaSquareFull
                      style={{ color: `${val?.color}`, height: '14px', width: '14px' }}
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <>
                        <Typography component="span" sx={{ fontWeight: 500, fontSize: "16px", letterSpacing: "-0.4px" }}>
                          {val?.name}:{' '}
                        </Typography>
                        <Typography component="span" sx={{ fontWeight: 500, fontSize: "16px", color: "#182A4E" }}>
                          {val?.value}
                        </Typography>
                      </>
                    }
                  />
                </ListItem>
              ))}
            </List>

            <Box sx={{ flexShrink: 0 }}>
              <PieChart highcharts={Highcharts} options={options} />
            </Box>
          </>
        )}

      </Box>
      {open &&

        <IssuesPopup title={'Issues Distribution Severity'} value={popupData.name} status={'Severity'} onOpen={open} onClose={() => setOpen(false)} />

      }
    </Box>

  );
};

export default OpenIssueDistributionGraph;